import { Button, Form, Input, Select, Upload } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { Publication } from "@bindeleddet/core/types";
import { uploadButton } from "components/UploadButton/UploadButton";
import { getBase64 } from "helpers/functions";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";

const { Option } = Select;

interface Props {
  newPublication: boolean;
  publication?: Publication;
  handleCreateOrEdit: (publication: Publication) => void;
}

const CreateOrEditPublication: React.FC<Props> = ({ newPublication, publication, handleCreateOrEdit }) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [removeFile, setRemoveFile] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (publication) {
      form.setFieldsValue({
        title: publication.title,
        description: publication.description,
        publication_type: publication.publication_type,
      });
      setFile(null);
      setPreview(null);
      setRemoveFile(false);
    }
  }, [publication, form]);

  const publishPublication = (values: Publication) => {
    if (publication) return;
    setLoading(true);

    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      formData.append(key, String(value));
    });

    formData.append("publication_date", new Date().toISOString());

    if (file) {
      formData.append("publication_file", file);
    }

    axios
      .post<Publication>("/website-contents/publications/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(response => {
        message("success", "Publikasjon publisert");
        handleCreateOrEdit(response.data);
      })
      .catch(() => message("error", "Kunne ikke publisere publikasjon"))
      .finally(() => setLoading(false));
  };

  const updatePublication = (values: Publication) => {
    if (!publication) return;
    setLoading(true);

    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      formData.append(key, String(value));
    });

    if (file) {
      formData.append("publication_file", file);
    }
    if (removeFile) {
      formData.append("publication_file", "");
    }

    axios
      .patch<Publication>(`/website-contents/publications/${publication.id}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(response => {
        message("success", "Publikasjon oppdatert");
        handleCreateOrEdit(response.data);
      })
      .catch(() => message("error", "Kunne ikke oppdatere publikasjon"))
      .finally(() => setLoading(false));
  };

  useEffect(
    function setPreviewImage() {
      if (file) {
        getBase64(file)
          .then((result: string | null) => {
            setPreview(result);
          })
          .catch(() => {
            setPreview(null);
          });
      }
    },
    [file]
  );

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={newPublication ? publishPublication : updatePublication}
    >
      <Form.Item label="Tittel" name="title" rules={[{ required: true, message: "Skriv inn tittel" }]}>
        <Input placeholder="Skriv inn tittel..." />
      </Form.Item>
      <Form.Item label="Beskrivelse" name="description">
        <ReactQuill theme="snow" />
      </Form.Item>
      <Form.Item label="Type" name="publication_type" rules={[{ required: true, message: "Velg type publikasjon" }]}>
        <Select placeholder="Velg type publikasjon">
          <Option value="" disabled>
            Velg type publikasjon
          </Option>
          <Option value={1}>Alumniundersøkelsen</Option>
          <Option value={2}>Diplomundersøkelsen</Option>
          <Option value={3}>Covid 19-undersøkelsen</Option>
        </Select>
      </Form.Item>
      <Form.Item label="Fil" rules={[{ required: true, message: "Legg til en fil" }]}>
        <Upload
          listType="picture-card"
          className="avatar-uploader"
          maxCount={2}
          beforeUpload={f => {
            setFile(f);
            setRemoveFile(false);
            return false;
          }}
          onRemove={() => {
            setFile(null);
            setPreview(null);
            setRemoveFile(true);
          }}
          fileList={
            removeFile
              ? []
              : file
              ? preview
                ? [
                    {
                      uid: "-1",
                      name: file?.name ?? "Ny fil",
                      url: preview,
                      status: "done",
                    },
                  ]
                : []
              : publication?.publication_file
              ? [
                  {
                    uid: "-1",
                    name:
                      publication?.publication_file?.substring(publication?.publication_file?.lastIndexOf("/") + 1) ||
                      "Fil",
                    status: "done",
                    url: publication?.publication_file,
                  },
                ]
              : []
          }
        >
          {uploadButton}
        </Upload>
      </Form.Item>
      <Button type="primary" htmlType="submit" loading={loading} style={{ width: "100%" }}>
        {newPublication ? "Publiser publikasjon" : "Oppdater publikasjon"}
      </Button>
    </Form>
  );
};

export default CreateOrEditPublication;
